/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { 
  useUserSettingsByUserId, 
  useUpdateUserSettingsByUserId, 
  useCreateUserSettings
} from "@/shared/hooks/api/useUserSettings";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";

// Chave para armazenar preferência de idioma no localStorage
const LANGUAGE_PREFERENCE_KEY = 'prodgy-user-language-preference';

// Função para salvar a preferência de idioma no localStorage
const saveLanguagePreference = (language: string): void => {
  try {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, language);
  } catch (e) {
    console.error("[LanguageSettings] Não foi possível salvar no localStorage", e);
  }
};

interface LanguageSettingsProps {
  /**
   * `compact` tira a caixa e o rótulo e deixa só o seletor, para caber no
   * cabeçalho do perfil (AB#85634). O rótulo some porque o próprio valor —
   * "Português", "English" — já diz o que o campo é.
   */
  variant?: "card" | "compact";
}

export const LanguageSettings = ({ variant = "card" }: LanguageSettingsProps = {}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState<string>("pt");

  // Usar hooks para buscar e atualizar configurações
  const { 
    data: userSettings, 
    isLoading, 
    error: fetchError 
  } = useUserSettingsByUserId(user?.id);
  
  const { mutate: updateUserSettings, isPending: _isUpdating } = useUpdateUserSettingsByUserId();
  const { mutate: createUserSettings, isPending: _isCreating } = useCreateUserSettings();

  // Carregar idioma quando as configurações do usuário forem carregadas
  useEffect(() => {
    if (userSettings) {
      // Se temos configurações, definir o idioma a partir delas
      const userLanguage = userSettings.language || "pt";
      setLanguage(userLanguage);
      i18n.changeLanguage(userLanguage);
      // Salvar a preferência no localStorage
      saveLanguagePreference(userLanguage);
    } else if (!isLoading && user?.id) {
      // Se não temos configurações mas a busca terminou e temos um usuário,
      // criar configurações com idioma padrão
      createUserSettings({
        language: "pt"
      });
      i18n.changeLanguage("pt");
    }
  }, [userSettings, isLoading, user, i18n, createUserSettings]);

  // Tratar erros de busca
  useEffect(() => {
    if (fetchError) {
      console.error("Erro ao buscar configuração de idioma:", fetchError);
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.languageError"),
      });
    }
  }, [fetchError, toast, t]);

  const handleLanguageChange = async (newLanguage: string) => {
    try {
      if (user?.id) {
        if (userSettings) {
          // Atualizar configurações existentes
          updateUserSettings(
            {
              userId: user.id,
              data: { language: newLanguage }
            },
            {
              onSuccess: () => {
                setLanguage(newLanguage);
                i18n.changeLanguage(newLanguage);
                saveLanguagePreference(newLanguage);
                toast({
                  title: t("common.success"),
                  description: t("profile.languageSuccess"),
                });
              },
              onError: (error: any) => {
                console.error("Erro ao atualizar idioma:", error);
                toast({
                  variant: "destructive",
                  title: t("common.error"),
                  description: resolveApiErrorMessage(error, t),
                });
              }
            }
          );
        } else {
          // Criar novas configurações
          createUserSettings(
            { 
              language: newLanguage,
              theme: 'light' // Definir um tema padrão
            },
            {
              onSuccess: () => {
                setLanguage(newLanguage);
                i18n.changeLanguage(newLanguage);
                saveLanguagePreference(newLanguage);
                toast({
                  title: t("common.success"),
                  description: t("profile.languageSuccess"),
                });
              },
              onError: (error: any) => {
                console.error("Erro ao criar configurações de idioma:", error);
                toast({
                  variant: "destructive",
                  title: t("common.error"),
                  description: resolveApiErrorMessage(error, t),
                });
              }
            }
          );
        }
      }
    } catch (error: any) {
      console.error("Erro ao processar alteração de idioma:", error);
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    }
  };

  if (isLoading) return null;

  const opcoes = (
    <SelectContent>
      {/* Sem bandeira, de propósito: bandeira é país, não idioma. Português é do
          Brasil ou de Portugal? Inglês, de qual país? O nome escrito no próprio
          idioma é o que quem procura o próprio idioma reconhece. */}
      <SelectItem value="en">{t("profile.languages.en")}</SelectItem>
      <SelectItem value="pt">{t("profile.languages.pt")}</SelectItem>
    </SelectContent>
  );

  if (variant === "compact") {
    return (
      <Select value={language} onValueChange={handleLanguageChange}>
        <SelectTrigger className="h-8 w-auto gap-2 border-none bg-transparent px-2 text-sm text-muted-foreground hover:bg-accent/50 focus:ring-0 focus:ring-offset-0">
          <Languages className="h-4 w-4" />
          <SelectValue placeholder={t("profile.language")} />
        </SelectTrigger>
        {opcoes}
      </Select>
    );
  }

  return (
    <div className="flex items-center gap-4 rounded-lg bg-secondary/50 px-5 py-4">
      <Languages className="h-5 w-5 text-muted-foreground" />
      <div className="flex-1">
        <p className="text-sm font-medium">{t("profile.language")}</p>
        <Select value={language} onValueChange={handleLanguageChange}>
          <SelectTrigger className="mt-1 h-8 w-[180px]">
            <SelectValue placeholder={t("profile.language")} />
          </SelectTrigger>
          {opcoes}
        </Select>
      </div>
    </div>
  );
};
